import React, { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { Users, BookOpen, BookHeart, Check, X, Trash2, Pencil, Sparkles, Loader2, HandHeart, KeyRound, Search, Copy, Star } from "lucide-react";
import { toast } from "sonner";

const TABS = ["Members", "Devotionals", "Word & Wellness", "Announcements", "Verse", "Give", "Resets", "Moderation"];

export default function Admin() {
  const [tab, setTab] = useState("Members");
  const [stats, setStats] = useState(null);

  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { loadStats(); }, [tab]);

  return (
    <div className="space-y-8">
      <div className="fade-up">
        <p className="text-xs tracking-[0.2em] uppercase text-[#C46B4E] font-semibold">Leader Tools</p>
        <h1 className="font-heading text-5xl text-[#2A2621] mt-2">Admin dashboard</h1>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Stat icon={Users} label="Members" value={stats.total_members} />
          <Stat icon={Users} label="Pending" value={stats.pending_members} accent />
          <Stat icon={BookHeart} label="Devotionals" value={stats.devotionals} />
          <Stat icon={BookOpen} label="Word & Wellness" value={stats.videos} />
          <Stat icon={HandHeart} label="Prayers" value={stats.prayers} />
          <Stat icon={Sparkles} label="Praise" value={stats.testimonies} />
          <Stat icon={KeyRound} label="Reset Requests" value={stats.reset_requests || 0} accent />
        </div>
      )}

      <div className="flex flex-wrap gap-2 border-b border-[#D5CEBF] pb-3">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.replace(/\s/g, "-")}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === t ? "bg-[#3D5844] text-white" : "text-[#615A50] hover:bg-[#F3EFE4]"}`}>{t}</button>
        ))}
      </div>

      {tab === "Members" && <MembersTab onChange={loadStats} />}
      {tab === "Devotionals" && <DevotionalsTab onChange={loadStats} />}
      {tab === "Word & Wellness" && <VideosTab onChange={loadStats} />}
      {tab === "Announcements" && <AnnouncementsTab />}
      {tab === "Verse" && <VerseTab />}
      {tab === "Give" && <GiveTab />}
      {tab === "Resets" && <ResetRequestsTab onChange={loadStats} />}
      {tab === "Moderation" && <ModerationTab />}
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-2xl border p-5 ${accent && value > 0 ? "border-[#C46B4E] bg-[#C46B4E]/5" : "border-[#D5CEBF] bg-[#F3EFE4]"}`}>
      <Icon className={`w-5 h-5 ${accent && value > 0 ? "text-[#C46B4E]" : "text-[#3D5844]"}`} />
      <p className="font-heading text-3xl text-[#2A2621] mt-2">{value}</p>
      <p className="text-sm text-[#615A50]">{label}</p>
    </div>
  );
}

function MembersTab({ onChange }) {
  const [members, setMembers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/members").then((r) => setMembers(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try {
      if (action === "delete") {
        if (!window.confirm("Remove this member permanently?")) return;
        await api.delete(`/admin/members/${id}`);
      } else await api.post(`/admin/members/${id}/${action}`);
      toast.success("Done");
      load(); onChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  const update = async (form) => {
    try { await api.put(`/admin/members/${editing.user_id}`, form); toast.success("Member updated"); setEditing(null); load(); onChange(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const term = q.trim().toLowerCase();
  const match = (m) => !term || (m.name || "").toLowerCase().includes(term) || (m.email || "").toLowerCase().includes(term);
  const pending = members.filter((m) => m.status === "pending" && match(m));
  const others = members.filter((m) => m.status !== "pending" && match(m));

  return (
    <div className="space-y-6">
      <div className="relative max-w-md">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#615A50]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-member-search"
          placeholder="Search members by name or email…"
          className="w-full rounded-full border border-[#D5CEBF] bg-white pl-10 pr-4 py-2.5 text-[#2A2621] outline-none focus:ring-2 focus:ring-[#3D5844]/40" />
      </div>
      {pending.length > 0 && (
        <div>
          <h3 className="font-heading text-2xl text-[#2A2621] mb-3">Pending requests ({pending.length})</h3>
          <div className="space-y-3">
            {pending.map((m) => (
              <div key={m.user_id} className="rounded-xl border border-[#C46B4E]/40 bg-[#C46B4E]/5 p-5" data-testid={`admin-member-${m.user_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-[#2A2621]">{m.name} <span className="text-sm text-[#615A50]">· {m.email}</span></p>
                    {m.reason && <p className="text-sm text-[#615A50] mt-1 italic">"{m.reason}"</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => act(m.user_id, "approve")} data-testid={`admin-approve-${m.user_id}`}
                      className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-[#3D5844] text-white text-sm font-medium hover:bg-[#2F4535]"><Check className="w-4 h-4" /> Approve</button>
                    <button onClick={() => act(m.user_id, "reject")} data-testid={`admin-reject-${m.user_id}`}
                      className="inline-flex items-center gap-1 px-4 py-2 rounded-lg border border-[#D5CEBF] text-[#615A50] text-sm font-medium hover:bg-[#E7DFCE]"><X className="w-4 h-4" /> Decline</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div>
        <h3 className="font-heading text-2xl text-[#2A2621] mb-3">All members ({others.length})</h3>
        <div className="space-y-2">
          {others.map((m) => (
            <div key={m.user_id} className="flex items-center justify-between rounded-xl border border-[#D5CEBF] bg-[#FAF7EE] p-4" data-testid={`admin-member-row-${m.user_id}`}>
              <div>
                <p className="font-medium text-[#2A2621]">{m.name} <span className="text-sm text-[#615A50]">· {m.email}</span></p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${m.role === "admin" ? "bg-[#3D5844] text-white" : m.status === "approved" ? "bg-[#E7DFCE] text-[#3D5844]" : "bg-[#C46B4E]/15 text-[#C46B4E]"}`}>{m.role === "admin" ? "leader" : m.status}</span>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setEditing(m)} data-testid={`admin-member-edit-${m.user_id}`} className="p-2 text-[#615A50] hover:text-[#3D5844]"><Pencil className="w-4 h-4" /></button>
                {m.role !== "admin" && (
                  <button onClick={() => act(m.user_id, "delete")} data-testid={`admin-member-delete-${m.user_id}`} className="p-2 text-[#615A50] hover:text-[#AC5B40]"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {editing && (
        <EditModal title={`Edit ${editing.name}`} testid="admin-edit-member"
          fields={[
            { key: "name", label: "Name" },
            { key: "role", label: "Role", type: "select", options: ["member", "admin"] },
            { key: "status", label: "Status", type: "select", options: ["approved", "pending", "rejected"] },
          ]}
          initial={{ name: editing.name, role: editing.role, status: editing.status }}
          onSave={update} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

function FormCard({ title, fields, onSubmit, testid }) {
  const [form, setForm] = useState(Object.fromEntries(fields.map((f) => [f.key, f.default ?? ""])));
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    await onSubmit(form);
    setForm(Object.fromEntries(fields.map((f) => [f.key, f.default ?? ""])));
  };
  return (
    <form onSubmit={submit} className="rounded-2xl border border-[#D5CEBF] bg-[#F3EFE4] p-6 space-y-3" data-testid={testid}>
      <h3 className="font-heading text-2xl text-[#2A2621]">{title}</h3>
      {fields.map((f) => (
        f.type === "textarea" ? (
          <textarea key={f.key} value={form[f.key]} onChange={set(f.key)} placeholder={f.label} rows={3} required={f.required} data-testid={`${testid}-${f.key}`}
            className="w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#3D5844]/40 resize-none" />
        ) : f.type === "datetime-local" ? (
          <div key={f.key} className="space-y-1">
            <label className="text-sm text-[#615A50]">{f.label}</label>
            <input type="datetime-local" value={form[f.key]} onChange={set(f.key)} required={f.required} data-testid={`${testid}-${f.key}`}
              className="w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#3D5844]/40" />
          </div>
        ) : (
          <input key={f.key} type={f.type || "text"} value={form[f.key]} onChange={set(f.key)} placeholder={f.label} required={f.required} data-testid={`${testid}-${f.key}`}
            className="w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#3D5844]/40" />
        )
      ))}
      <button type="submit" className="rounded-xl bg-[#3D5844] text-white px-5 py-2.5 font-medium hover:bg-[#2F4535] transition-colors" data-testid={`${testid}-submit`}>Create</button>
    </form>
  );
}

function EditModal({ title, fields, initial, onSave, onClose, testid }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => { e.preventDefault(); await onSave(form); };
  return (
    <div className="fixed inset-0 z-50 bg-[#2A2621]/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FAF7EE] rounded-2xl border border-[#D5CEBF] w-full max-w-lg p-7 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-3xl text-[#2A2621]">{title}</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-[#615A50]" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3" data-testid={testid}>
          {fields.map((f) =>
            f.type === "textarea" ? (
              <textarea key={f.key} value={form[f.key] ?? ""} onChange={set(f.key)} placeholder={f.label} rows={3} data-testid={`${testid}-${f.key}`}
                className="w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#3D5844]/40 resize-none" />
            ) : f.type === "select" ? (
              <select key={f.key} value={form[f.key] ?? ""} onChange={set(f.key)} data-testid={`${testid}-${f.key}`}
                className="w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#3D5844]/40">
                {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : f.type === "datetime-local" ? (
              <div key={f.key} className="space-y-1">
                <label className="text-sm text-[#615A50]">{f.label}</label>
                <input type="datetime-local" value={form[f.key] ?? ""} onChange={set(f.key)} data-testid={`${testid}-${f.key}`}
                  className="w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#3D5844]/40" />
              </div>
            ) : (
              <input key={f.key} type={f.type || "text"} value={form[f.key] ?? ""} onChange={set(f.key)} placeholder={f.label} data-testid={`${testid}-${f.key}`}
                className="w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#3D5844]/40" />
            )
          )}
          <button type="submit" data-testid={`${testid}-save`} className="rounded-xl bg-[#3D5844] text-white px-5 py-2.5 font-medium hover:bg-[#2F4535] transition-colors">Save changes</button>
        </form>
      </div>
    </div>
  );
}

function ItemRow({ title, subtitle, onEdit, onDelete, testid, badge, onFeature, featured }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-[#D5CEBF] bg-[#FAF7EE] p-4" data-testid={testid}>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-[#2A2621] truncate">{title}</p>
          {badge && <span className="shrink-0 text-xs px-2 py-0.5 rounded-full bg-[#C46B4E]/15 text-[#C46B4E] font-medium" data-testid={`${testid}-badge`}>{badge}</span>}
          {featured && <span className="shrink-0 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#C46B4E] text-white font-medium"><Star className="w-3 h-3" /> Featured</span>}
        </div>
        {subtitle && <p className="text-sm text-[#615A50] truncate">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {onFeature && <button onClick={onFeature} data-testid={`${testid}-feature`} title={featured ? "Unpin" : "Pin to top"}
          className={`p-2 ${featured ? "text-[#C46B4E]" : "text-[#615A50] hover:text-[#C46B4E]"}`}><Star className={`w-4 h-4 ${featured ? "fill-[#C46B4E]" : ""}`} /></button>}
        {onEdit && <button onClick={onEdit} data-testid={`${testid}-edit`} className="p-2 text-[#615A50] hover:text-[#3D5844]"><Pencil className="w-4 h-4" /></button>}
        {onDelete && <button onClick={onDelete} data-testid={`${testid}-delete`} className="p-2 text-[#615A50] hover:text-[#AC5B40]"><Trash2 className="w-4 h-4" /></button>}
      </div>
    </div>
  );
}

function VideosTab({ onChange }) {
  const [videos, setVideos] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/study-videos?include_scheduled=true").then((r) => setVideos(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const fields = [
    { key: "title", label: "Video title (e.g. This Week: The Gospel of John)", required: true },
    { key: "youtube_url", label: "YouTube link (https://youtube.com/watch?v=…)", required: true },
    { key: "description", label: "Short description (optional)", type: "textarea" },
    { key: "notes", label: "Notes & discussion — verse list or questions members can follow (optional)", type: "textarea" },
    { key: "publish_at", label: "Publish date & time (leave blank to post now)", type: "datetime-local" },
  ];
  const norm = (form) => ({ ...form, publish_at: form.publish_at ? new Date(form.publish_at).toISOString() : "" });
  const toLocalInput = (iso) => {
    if (!iso) return "";
    const dt = new Date(iso);
    if (isNaN(dt.getTime())) return "";
    return new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };
  const create = async (form) => {
    try { await api.post("/study-videos", norm(form)); toast.success(form.publish_at ? "Word & Wellness scheduled" : "Video added"); load(); onChange(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const update = async (form) => {
    try { await api.put(`/study-videos/${editing.id}`, norm(form)); toast.success("Video updated"); setEditing(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const feature = async (id) => {
    try { await api.post(`/study-videos/${id}/feature`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Remove this Word & Wellness video?")) return;
    try { await api.delete(`/study-videos/${id}`); toast.success("Video removed"); load(); onChange(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <FormCard title="Add a Word & Wellness video" testid="admin-new-video" fields={fields} onSubmit={create} />
      <div className="space-y-2">
        {videos.length === 0 && <p className="text-sm text-[#615A50]">No videos yet. Paste a YouTube link to add one.</p>}
        {videos.map((v) => (
          <ItemRow key={v.id} testid={`admin-video-${v.id}`} title={v.title}
            subtitle={v.scheduled ? `Scheduled for ${new Date(v.publish_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : v.youtube_url}
            badge={v.scheduled ? "Scheduled" : null} featured={v.featured}
            onFeature={() => feature(v.id)} onEdit={() => setEditing(v)} onDelete={() => del(v.id)} />
        ))}
      </div>
      {editing && (
        <EditModal title="Edit video" testid="admin-edit-video"
          fields={fields.map((f) => ({ ...f, required: false }))}
          initial={{ title: editing.title, youtube_url: editing.youtube_url, description: editing.description || "", notes: editing.notes || "", publish_at: toLocalInput(editing.publish_at) }}
          onSave={update} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

function AnnouncementsTab() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/announcements").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async (form) => {
    try { await api.post("/announcements", form); toast.success("Announcement posted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const update = async (form) => {
    try { await api.put(`/announcements/${editing.id}`, form); toast.success("Announcement updated"); setEditing(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try { await api.delete(`/announcements/${id}`); toast.success("Announcement deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <FormCard title="Post announcement" testid="admin-new-announcement"
        fields={[{ key: "title", label: "Title", required: true }, { key: "body", label: "Message", type: "textarea", required: true }]}
        onSubmit={create} />
      <div className="space-y-3">
        {list.length === 0 && <p className="text-sm text-[#615A50]">No announcements yet.</p>}
        {list.map((a) => (
          <div key={a.id} className="rounded-xl border border-[#D5CEBF] bg-[#FAF7EE] p-4" data-testid={`admin-announcement-${a.id}`}>
            <div className="flex items-start justify-between gap-2">
              <p className="font-heading text-xl text-[#2A2621]">{a.title}</p>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => setEditing(a)} data-testid={`admin-announcement-${a.id}-edit`} className="p-1.5 text-[#615A50] hover:text-[#3D5844]"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => del(a.id)} data-testid={`admin-announcement-${a.id}-delete`} className="p-1.5 text-[#615A50] hover:text-[#AC5B40]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <p className="text-sm text-[#615A50] mt-1">{a.body}</p>
            <p className="text-xs text-[#3D5844] mt-2 font-medium" data-testid={`admin-announcement-${a.id}-stats`}>{a.read_count ?? 0} read · {a.dismiss_count ?? 0} dismissed</p>
          </div>
        ))}
      </div>
      {editing && (
        <EditModal title="Edit announcement" testid="admin-edit-announcement"
          fields={[{ key: "title", label: "Title" }, { key: "body", label: "Message", type: "textarea" }]}
          initial={{ title: editing.title, body: editing.body }}
          onSave={update} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

function DevotionalsTab({ onChange }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/devotionals?include_scheduled=true").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const fields = [
    { key: "title", label: "Title", required: true },
    { key: "scripture_ref", label: "Scripture reference (e.g. John 3:16 KJV)" },
    { key: "scripture_text", label: "Scripture text (KJV)", type: "textarea" },
    { key: "body", label: "Devotional message", type: "textarea", required: true },
    { key: "publish_at", label: "Publish date & time (leave blank to post now)", type: "datetime-local" },
  ];
  const norm = (form) => ({ ...form, publish_at: form.publish_at ? new Date(form.publish_at).toISOString() : "" });
  const toLocalInput = (iso) => {
    if (!iso) return "";
    const dt = new Date(iso);
    if (isNaN(dt.getTime())) return "";
    return new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };
  const create = async (form) => { try { await api.post("/devotionals", norm(form)); toast.success(form.publish_at ? "Devotional scheduled" : "Devotional posted"); load(); onChange(); } catch (e) { toast.error(apiError(e)); } };
  const update = async (form) => { try { await api.put(`/devotionals/${editing.id}`, norm(form)); toast.success("Devotional updated"); setEditing(null); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (id) => { if (!window.confirm("Delete this devotional?")) return; try { await api.delete(`/devotionals/${id}`); load(); onChange(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <FormCard title="Post a devotional" testid="admin-new-devotional" fields={fields} onSubmit={create} />
      <div className="space-y-2">
        {items.length === 0 && <p className="text-sm text-[#615A50]">No devotionals yet.</p>}
        {items.map((d) => (
          <ItemRow key={d.id} testid={`admin-devotional-${d.id}`} title={d.title}
            subtitle={(d.scheduled ? `Scheduled for ${new Date(d.publish_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : (d.scripture_ref || "Published")) + ` · ${d.read_count ?? 0} read`}
            badge={d.scheduled ? "Scheduled" : null}
            onEdit={() => setEditing(d)} onDelete={() => del(d.id)} />
        ))}
      </div>
      {editing && (
        <EditModal title="Edit devotional" testid="admin-edit-devotional"
          fields={fields.map((f) => ({ ...f, required: false }))}
          initial={{ title: editing.title, scripture_ref: editing.scripture_ref || "", scripture_text: editing.scripture_text || "", body: editing.body, publish_at: toLocalInput(editing.publish_at || editing.date) }}
          onSave={update} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

function VerseTab() {
  const [verse, setVerse] = useState({ ref: "", text: "" });
  const [current, setCurrent] = useState(null);
  useEffect(() => { api.get("/daily-verse").then((r) => setCurrent(r.data)).catch(() => {}); }, []);
  const save = async (e) => {
    e.preventDefault();
    try { await api.put("/admin/featured-verse", verse); toast.success("Verse of the day set"); setCurrent(verse); }
    catch (er) { toast.error(apiError(er)); }
  };
  const clear = async () => {
    try { await api.delete("/admin/featured-verse"); toast.success("Reverted to automatic daily verse"); const r = await api.get("/daily-verse"); setCurrent(r.data); }
    catch (er) { toast.error(apiError(er)); }
  };
  return (
    <div className="max-w-2xl space-y-6">
      <div className="rounded-2xl border border-[#3D5844]/25 bg-[#3D5844] text-[#FAF7EE] p-6" data-testid="admin-verse-current">
        <p className="text-xs uppercase tracking-widest text-[#FAF7EE]/80">Currently showing</p>
        {current && <><p className="font-heading text-2xl mt-2">"{current.text}"</p><p className="mt-2 text-[#FAF7EE]/85">— {current.ref}</p></>}
      </div>
      <form onSubmit={save} className="rounded-2xl border border-[#D5CEBF] bg-[#F3EFE4] p-6 space-y-3" data-testid="admin-verse-form">
        <h3 className="font-heading text-2xl text-[#2A2621]">Set a featured Verse of the Day</h3>
        <p className="text-sm text-[#615A50]">Leave this and choose "Use automatic" to let the app rotate through KJV verses.</p>
        <input value={verse.ref} onChange={(e) => setVerse({ ...verse, ref: e.target.value })} placeholder="Reference (e.g. Philippians 4:13)" data-testid="admin-verse-ref"
          className="w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#3D5844]/40" />
        <textarea value={verse.text} onChange={(e) => setVerse({ ...verse, text: e.target.value })} placeholder="Verse text (KJV)" rows={3} data-testid="admin-verse-text"
          className="w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#3D5844]/40 resize-none" />
        <div className="flex gap-2">
          <button type="submit" data-testid="admin-verse-save" className="rounded-xl bg-[#3D5844] text-white px-5 py-2.5 font-medium hover:bg-[#2F4535]">Set verse</button>
          <button type="button" onClick={clear} className="rounded-xl border border-[#D5CEBF] text-[#615A50] px-5 py-2.5 font-medium hover:bg-[#E7DFCE]">Use automatic</button>
        </div>
      </form>
    </div>
  );
}


function GiveTab() {
  const [methods, setMethods] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/donation-methods").then((r) => setMethods(r.data.methods || [])).catch((e) => toast.error(apiError(e))); }, []);
  const setField = (i, key, val) => setMethods((ms) => ms.map((m, idx) => (idx === i ? { ...m, [key]: val } : m)));
  const addRow = () => setMethods((ms) => [...ms, { id: `new_${Date.now()}`, label: "", value: "", note: "" }]);
  const removeRow = (i) => setMethods((ms) => ms.filter((_, idx) => idx !== i));
  const save = async () => {
    setSaving(true);
    try {
      const payload = { methods: methods.filter((m) => m.label.trim() && m.value.trim()).map((m) => ({ label: m.label, value: m.value, note: m.note || "" })) };
      const r = await api.put("/admin/donation-methods", payload);
      setMethods(r.data.methods || []);
      toast.success("Give options saved");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="max-w-2xl space-y-5" data-testid="admin-give-tab">
      <div>
        <h3 className="font-heading text-2xl text-[#2A2621]">Edit your Give options</h3>
        <p className="text-sm text-[#615A50] mt-1">These appear on the Give page. Add methods like Zelle, Venmo, or Txt2Give. Use the notes field for instructions (e.g. "Text GIVE to 555-123-4567").</p>
      </div>
      <div className="space-y-4">
        {methods.map((m, i) => (
          <div key={m.id} className="rounded-2xl border border-[#D5CEBF] bg-[#F3EFE4] p-5 space-y-3" data-testid={`admin-give-row-${i}`}>
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#3D5844]">Give option {i + 1}</p>
              <button onClick={() => removeRow(i)} data-testid={`admin-give-remove-${i}`} className="p-1.5 text-[#615A50] hover:text-[#C46B4E]" title="Remove"><Trash2 className="w-4 h-4" /></button>
            </div>
            <input value={m.label} onChange={(e) => setField(i, "label", e.target.value)} placeholder="Label (e.g. Zelle, Venmo, Txt2Give)" data-testid={`admin-give-label-${i}`}
              className="w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#3D5844]/40" />
            <input value={m.value} onChange={(e) => setField(i, "value", e.target.value)} placeholder="Value (email, @handle, phone number, or short code)" data-testid={`admin-give-value-${i}`}
              className="w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#3D5844]/40" />
            <textarea value={m.note || ""} onChange={(e) => setField(i, "note", e.target.value)} placeholder="Instructions (optional) — e.g. Text GIVE to 555-123-4567" rows={2} data-testid={`admin-give-note-${i}`}
              className="w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#3D5844]/40 resize-none" />
          </div>
        ))}
        {methods.length === 0 && <p className="text-sm text-[#615A50]">No give options yet. Add one below.</p>}
      </div>
      <div className="flex gap-2">
        <button onClick={addRow} data-testid="admin-give-add" className="rounded-xl border border-[#D5CEBF] text-[#615A50] px-5 py-2.5 font-medium hover:bg-[#E7DFCE]">+ Add give option</button>
        <button onClick={save} disabled={saving} data-testid="admin-give-save" className="rounded-xl bg-[#3D5844] text-white px-5 py-2.5 font-medium hover:bg-[#2F4535] disabled:opacity-60">{saving ? "Saving…" : "Save changes"}</button>
      </div>
    </div>
  );
}

function ResetRequestsTab({ onChange }) {
  const [reqs, setReqs] = useState([]);
  const [issued, setIssued] = useState(null); // { code, email, name }
  const load = () => api.get("/admin/reset-requests").then((r) => setReqs(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const issue = async (id) => {
    try {
      const { data } = await api.post(`/admin/reset-requests/${id}/issue`);
      setIssued(data);
      load(); onChange();
    } catch (e) { toast.error(apiError(e)); }
  };
  const dismiss = async (id) => {
    if (!window.confirm("Dismiss this reset request?")) return;
    try { await api.delete(`/admin/reset-requests/${id}`); toast.success("Request dismissed"); load(); onChange(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const copyCode = () => { navigator.clipboard?.writeText(issued.code); toast.success("Code copied"); };

  return (
    <div className="max-w-3xl space-y-5" data-testid="admin-resets-tab">
      <div className="rounded-2xl border border-[#D5CEBF] bg-[#F3EFE4] p-5 text-sm text-[#615A50]">
        <div className="flex items-center gap-2 text-[#C46B4E] mb-1"><KeyRound className="w-4 h-4" /><span className="font-semibold text-[#2A2621]">Password reset requests</span></div>
        When a member forgets their password, they request a reset here. Issue a one-time code and share it with them privately. The code expires in 24 hours.
      </div>
      {reqs.length === 0 && <p className="text-sm text-[#615A50]" data-testid="admin-resets-empty">No pending reset requests.</p>}
      <div className="space-y-2">
        {reqs.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#D5CEBF] bg-[#FAF7EE] p-4" data-testid={`admin-reset-${r.id}`}>
            <div className="min-w-0">
              <p className="font-medium text-[#2A2621] truncate">{r.name} <span className="text-sm text-[#615A50]">· {r.email}</span></p>
              <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === "issued" ? "bg-[#3D5844] text-white" : "bg-[#C46B4E]/15 text-[#C46B4E]"}`}>{r.status === "issued" ? "code issued" : "awaiting code"}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => issue(r.id)} data-testid={`admin-reset-issue-${r.id}`}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-[#3D5844] text-white text-sm font-medium hover:bg-[#2F4535]"><KeyRound className="w-4 h-4" /> {r.status === "issued" ? "New code" : "Issue code"}</button>
              <button onClick={() => dismiss(r.id)} data-testid={`admin-reset-dismiss-${r.id}`} className="p-2 text-[#615A50] hover:text-[#AC5B40]"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
      {issued && (
        <div className="fixed inset-0 z-50 bg-[#2A2621]/40 flex items-center justify-center p-4" onClick={() => setIssued(null)}>
          <div className="bg-[#FAF7EE] rounded-2xl border border-[#D5CEBF] w-full max-w-md p-7 text-center" onClick={(e) => e.stopPropagation()} data-testid="admin-reset-code-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl text-[#2A2621]">Reset code for {issued.name || issued.email}</h2>
              <button onClick={() => setIssued(null)}><X className="w-5 h-5 text-[#615A50]" /></button>
            </div>
            <p className="text-sm text-[#615A50]">Share this one-time code with {issued.email} privately. It expires in 24 hours.</p>
            <div className="my-5 rounded-xl border border-[#3D5844]/25 bg-[#3D5844] text-[#FAF7EE] py-5 font-mono text-3xl tracking-[0.4em]" data-testid="admin-reset-code-value">{issued.code}</div>
            <button onClick={copyCode} data-testid="admin-reset-code-copy" className="inline-flex items-center gap-2 rounded-xl border border-[#D5CEBF] text-[#2A2621] px-5 py-2.5 font-medium hover:bg-[#E7DFCE]"><Copy className="w-4 h-4" /> Copy code</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ModerationTab() {
  const [text, setText] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true); setResult("");
    try { const { data } = await api.post("/ai/moderate", { text }); setResult(data.result); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  return (
    <div className="max-w-2xl rounded-2xl border border-[#D5CEBF] bg-[#F3EFE4] p-6">
      <div className="flex items-center gap-2 text-[#C46B4E]"><Sparkles className="w-5 h-5" /><h3 className="font-heading text-2xl text-[#2A2621]">AI Moderation Assistant</h3></div>
      <p className="text-sm text-[#615A50] mt-1">Paste any message to check whether it's respectful and safe for the community.</p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} data-testid="moderation-input" placeholder="Paste content to review…"
        className="mt-4 w-full rounded-xl border border-[#D5CEBF] bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#3D5844]/40 resize-none" />
      <button onClick={run} disabled={loading || !text.trim()} data-testid="moderation-run"
        className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#3D5844] text-white text-sm font-medium hover:bg-[#2F4535] disabled:opacity-60">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Review content
      </button>
      {result && <div className="mt-4 rounded-lg bg-[#FAF7EE] border border-[#D5CEBF] p-4 text-[#2A2621] whitespace-pre-wrap" data-testid="moderation-result">{result}</div>}
    </div>
  );
}
